'use client';
import { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const stats = [
  { value: 10, suffix: '+', label: 'Years of Experience', icon: 'CalendarIcon' },
  { value: 200, suffix: '+', label: 'Projects Delivered', icon: 'RocketLaunchIcon' },
  { value: 50, suffix: '+', label: 'Happy Clients', icon: 'UserGroupIcon' },
  { value: 30, suffix: '+', label: 'Technologies Mastered', icon: 'CpuChipIcon' },
];

const highlights = [
  { icon: 'CodeBracketIcon', text: 'Hands-on experience with 30+ modern technologies.' },
  { icon: 'GlobeAltIcon', text: 'High-Performance Websites & Web Applications' },
  { icon: 'DevicePhoneMobileIcon', text: 'Android & Cross-Platform Apps' },
  { icon: 'CloudIcon', text: 'Enterprise Cloud & DevOps Architecture' },
  { icon: 'SwatchIcon', text: 'Brand Identity & Motion Graphics' },
  { icon: 'ShoppingCartIcon', text: 'Full E-Commerce Ecosystems' },
];

function CountUpNumber({ target, suffix }: { target: number; suffix: string }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
          let start = 0;
          const duration = 1800;
          const step = (timestamp: number, startTime: number) => {
            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) {
              requestAnimationFrame((t) => step(t, startTime));
            }
          };
          requestAnimationFrame((t) => step(t, t));
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, started]);

  return (
    <div ref={ref} className="stat-number gradient-text">
      {count}
      {suffix}
    </div>
  );
}

export default function AboutSection() {
  return (
    <section id="about" className="py-16 lg:py-20 relative">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-20 initially-hidden animate-in">
          <div className="section-label mb-3">About Me</div>
          <h2
            className="text-4xl md:text-6xl font-extrabold"
            style={{ color: 'var(--text-primary)', letterSpacing: '-0.04em' }}
          >
            The Developer Behind
            <br />
            <span className="gradient-text">The Magic</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          {/* Left: Image + Code snippet */}
          <div className="relative initially-hidden animate-in-left">
            <div
              className="rounded-[32px] overflow-hidden relative"
              style={{
                height: '480px',
                background: 'linear-gradient(135deg, rgba(108,99,255,0.1), rgba(0,212,255,0.05))',
                border: '1px solid var(--glass-border)',
              }}
            >
              <AppImage
                src="https://images.unsplash.com/photo-1487338875411-8880f74114a2?auto=format&fit=crop&w=1000&q=80"
                alt="Developer workspace with multiple monitors showing code and design work"
                fill
                className="object-cover opacity-60"
              />

              {/* Overlay */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(to top, rgba(8,11,20,0.95) 0%, rgba(8,11,20,0.3) 50%, transparent 100%)',
                }}
              />

              {/* Code snippet overlay */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="code-snippet">
                  <div style={{ color: 'var(--text-muted)' }}>// My philosophy</div>
                  <div>
                    <span style={{ color: '#6C63FF' }}>const</span>{' '}
                    <span style={{ color: '#10D9A0' }}>approach</span>{' '}
                    <span style={{ color: '#F0F2FF' }}>= {'{'}</span>
                  </div>
                  <div className="pl-4">
                    <span style={{ color: '#00D4FF' }}>quality</span>
                    <span style={{ color: '#F0F2FF' }}>: </span>
                    <span style={{ color: '#F59E0B' }}>&quot;uncompromising&quot;</span>,
                  </div>
                  <div className="pl-4">
                    <span style={{ color: '#00D4FF' }}>delivery</span>
                    <span style={{ color: '#F0F2FF' }}>: </span>
                    <span style={{ color: '#F59E0B' }}>&quot;on-time, every time&quot;</span>
                  </div>
                  <div style={{ color: '#F0F2FF' }}>{'}'}</div>
                </div>
              </div>
            </div>

            {/* Decorative element */}
            <div
              className="absolute -top-6 -right-6 w-24 h-24 rounded-2xl glass-card flex items-center justify-center"
              style={{ animation: 'float-1 6s ease-in-out infinite' }}
            >
              <span className="text-4xl">🏆</span>
            </div>
          </div>

          {/* Right: Content */}
          <div className="space-y-8 initially-hidden animate-in-right">
            <div>
              <h3
                className="text-2xl md:text-3xl font-bold mb-4"
                style={{ color: 'var(--text-primary)', letterSpacing: '-0.03em' }}
              >
                I don&apos;t just build products —
                <br />
                <span className="gradient-text">I build competitive advantages.</span>
              </h3>
              <p
                className="text-base leading-relaxed mb-4"
                style={{ color: 'var(--text-secondary)' }}
              >
                With 10+ years of hands-on experience, I build high-performance websites, scalable
                web apps, and mobile applications, combining engineering expertise with UI/UX design
                to deliver fast, secure, and visually exceptional digital products.
              </p>
              <p className="text-base leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                I handle the full development lifecycle — from concept and design to development,
                optimization, deployment, and live production across websites, web apps, cloud/VPS,
                DevOps, and eCommerce, covering performance optimization, troubleshooting, and
                scaling for businesses and startups worldwide.
              </p>
            </div>

            {/* Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {highlights.map(({ icon, text }) => (
                <div
                  key={text}
                  className="flex items-center gap-3 p-3 rounded-xl"
                  style={{
                    background: 'rgba(108,99,255,0.05)',
                    border: '1px solid rgba(108,99,255,0.1)',
                  }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(108,99,255,0.15)' }}
                  >
                    <Icon name={icon as 'CodeBracketIcon'} size={16} className="text-[#6C63FF]" />
                  </div>
                  <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                    {text}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex gap-4 flex-wrap">
              <a href="#contact">
                <button className="btn-primary flex items-center gap-2 relative z-10">
                  <Icon name="ChatBubbleLeftRightIcon" size={18} />
                  <span>Let&apos;s Talk</span>
                </button>
              </a>
              <a href="#portfolio">
                <button className="btn-ghost flex items-center gap-2">
                  <Icon name="FolderOpenIcon" size={18} />
                  <span>See My Work</span>
                </button>
              </a>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map(({ value, suffix, label, icon }, i) => (
            <div
              key={label}
              className="glass-card p-6 text-center initially-hidden animate-in gradient-border"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4"
                style={{
                  background: 'linear-gradient(135deg, rgba(108,99,255,0.2), rgba(0,212,255,0.1))',
                }}
              >
                <Icon name={icon as 'CalendarIcon'} size={22} className="text-[#6C63FF]" />
              </div>
              <CountUpNumber target={value} suffix={suffix} />
              <div className="text-sm mt-2 font-medium" style={{ color: 'var(--text-muted)' }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
